import React from 'react'
import { useStaticQuery, graphql, Link } from 'gatsby'
import Divider from './Divider'

interface StatementsData {
  allMdx: {
    nodes: Array<{
      id: string
      frontmatter: {
        title: string
        excerpt?: string
        date: string
      }
      fields: {
        slug: string
      }
    }>
  }
}

const StatementsList: React.FC = () => {
  const data = useStaticQuery<StatementsData>(graphql`
    query StatementsListQuery {
      allMdx(
        filter: { internal: { contentFilePath: { regex: "/content/statements/" } } }
        sort: { frontmatter: { date: DESC } }
      ) {
        nodes {
          id
          frontmatter {
            title
            excerpt
            date
          }
          fields {
            slug
          }
        }
      }
    }
  `)

  const statements = data.allMdx.nodes

  // Group statements by year
  const groupedStatements = statements.reduce((acc, statement) => {
    const year = new Date(statement.frontmatter.date).getFullYear()
    if (!acc[year]) {
      acc[year] = []
    }
    acc[year].push(statement)
    return acc
  }, {} as Record<number, typeof statements>)

  const years = Object.keys(groupedStatements).map(Number).sort((a, b) => b - a)

  if (statements.length === 0) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-semibold text-text-light mb-4">
          No statements found
        </h2>
        <p className="text-text-light">
          Committee statements will appear here once they're published.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {years.map((year) => (
        <section key={year}>
          <h2 className="text-2xl font-heading font-bold text-primary mb-4">
            {year}
          </h2>
          <div className="space-y-3">
            {groupedStatements[year].map((statement) => {
              return (
                <Link
                  to={statement.fields.slug}
                  key={statement.id}
                  className="block mb-4"
                >
                  <article className="bg-gray-50 rounded-lg px-4 py-6 hover:bg-gray-100 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <h2 className="font-semibold text-lg md:text-xl mb-2 mt-0">
                        {statement.frontmatter.title}
                      </h2>
                      <div className="flex flex-wrap items-baseline gap-2 text-sm text-text-light">
                        <span>
                          {new Date(statement.frontmatter.date).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        {statement.frontmatter.excerpt && (
                          <>
                            <span>•</span>
                            <span className="line-clamp-2">
                              {statement.frontmatter.excerpt}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="text-primary font-medium text-sm whitespace-nowrap self-center">
                      Read →
                    </div>
                  </div>
                </article>
                </Link>
              )
            })}
          </div>
          <Divider />
        </section>
      ))}
    </div>
  )
}

export default StatementsList